<template>
  <private-gym-page
    id="admin-news-composer-page"
    name="admin-news-composer"
    :navbar="false"
  >
    <!--
      The shared PageTopNavbar is deliberately switched off. It carries the panel
      toggle and the logo, which is right for a member's landing surface and
      wrong here: this is a pushed detail screen and the author needs a way back
      to where they came from.
    -->
    <f7-navbar :title="pageTitle" back-link="Terug"></f7-navbar>

    <div class="admin-news-column">
      <!-- 1. Loading, edit mode only. -->
      <f7-block v-if="loading" class="text-align-center">
        <!--
          The INLINE preloader, never the app-wide overlay: that overlay covers
          the navbar and swallows the back gesture, so page chrome and title
          render immediately behind this block instead.
        -->
        <f7-preloader></f7-preloader>
      </f7-block>

      <!-- 2. The load failed. -->
      <f7-block v-else-if="error" strong inset>
        <!--
          PERSISTENT, NOT A TOAST. A load failure leaves the author with nothing
          else to act on, so the message must not disappear on a timer.

          The screen dispatches no notification of its own: `news/fetch_all_admin`
          underneath `news/find` is deliberately not silent (43-06), so the
          global handler has already shown the one message this failure gets.

          The server's own error text is deliberately NOT rendered. It is
          English, unbounded, and names nothing the author can act on.
        -->
        <p class="admin-news-heading">We konden dit bericht niet laden.</p>
        <p>Controleer je internetverbinding en probeer het opnieuw.</p>
        <f7-button fill @click="loadItem">Opnieuw proberen</f7-button>
      </f7-block>

      <!-- 3. The composer. -->
      <template v-else>
        <div class="px-2 pb-2">
          <!--
            Inherits f7-segmented's own active styling and is given no accent
            surface of its own: the page's single black fill is the save button.
          -->
          <f7-segmented strong tag="p" class="my-0">
            <f7-button :active="pane === 'edit'" @click="pane = 'edit'">
              Bewerken
            </f7-button>
            <f7-button :active="pane === 'preview'" @click="pane = 'preview'">
              Voorbeeld
            </f7-button>
          </f7-segmented>
        </div>

        <news-composer
          v-show="pane === 'edit'"
          :title="draft.title"
          :subtitle="draft.subtitle"
          :content="draft.content"
          :visible_from="draft.visible_from"
          :visible_to="draft.visible_to"
          :sanitisation="sanitisation"
          :saving="saving"
          @change="onDraftChange"
        ></news-composer>

        <f7-block v-if="pane === 'preview'" strong inset class="news-preview">
          <p v-if="!hasSomethingToPreview">
            Nog niets om te tonen. Schrijf eerst je bericht onder "Bewerken".
          </p>

          <template v-else>
            <p class="news-preview-title">{{ draft.title }}</p>
            <p class="news-preview-subtitle">{{ draft.subtitle }}</p>

            <!--
              THE SAME `.news-content` SCOPE THE MEMBER'S CARD USES — one
              definition, in src/css/components/news-content.scss, so the preview
              cannot drift from how the article actually lands.

              It renders the CURRENT DRAFT rather than the saved value, so the
              author can check their formatting before committing to it.
            -->
            <div class="news-content" v-html="previewBody"></div>

            <!--
              Deliberately NOT a pixel replica of the green card header: that
              header is a fixed presentation which cannot vary per item, and
              building a fake one only invites it to drift from the real one. The
              footnote says so in plain Dutch rather than leaving the author to
              wonder where it went.
            -->
            <f7-block-footer>
              Zo leest je bericht in de app. De kop en ondertitel staan daar op de groene kaart.
            </f7-block-footer>
          </template>
        </f7-block>

        <f7-block>
          <!--
            The one black fill on this page. Framework7's own preloader/loading
            props carry the in-flight state, and `disabled` is what prevents the
            double submit — never the app-wide overlay, which covers the navbar.
          -->
          <f7-button
            fill
            large
            :preloader="saving"
            :loading="saving"
            :disabled="saving"
            @click="save"
          >
            Bericht opslaan
          </f7-button>
        </f7-block>

        <!--
          B14 — THE NEWS-ITEM PUSH ACTION (NEWS-06, D-44-07, CD-09).

          BETWEEN THE SAVE BUTTON AND THE DELETE BLOCK, sharing the delete
          block's `hasStoredItem` flag. The position is the point: the
          destructive control stays LAST and ALONE, and there is nothing to
          announce until there is a stored item.

          Exactly one body renders. See `pushBlockBody` for which and why.
        -->
        <template v-if="hasStoredItem">
          <f7-block-title>Pushbericht</f7-block-title>

          <f7-block class="news-push-block">
            <p class="news-push-body">{{ pushBlockText }}</p>

            <!--
              THE SEND'S OWN FAILURE, AND ONLY THE SEND'S. `broadcasts/send`
              suppresses the global toast (IC8), so without this line a refused
              send would be silent. `broadcasts/confirm` deliberately does NOT
              suppress its toast — its 422 carries the form request's own field
              bag, which names the offending field, and nothing this screen could
              author would beat that — so a failed confirm gets the toast and no
              second surface here.
            -->
            <p v-if="pushFailure" class="news-push-failure">{{ pushFailure }}</p>

            <f7-button
              :disabled="!canSendPush"
              :preloader="pushing"
              :loading="pushing"
              @click="openPushConfirm"
            >
              Push versturen…
            </f7-button>
          </f7-block>
        </template>

        <!--
          EDIT MODE ONLY, below the save button and separated from it. The only
          red on the screen, alongside the destructive button inside each dialog.

          Delete lives here rather than on a list row: one confirmation path
          rather than two to keep in sync, and the author is already looking at
          what they are about to remove.
        -->
        <f7-block v-if="hasStoredItem">
          <f7-button color="red" @click="confirmDelete">
            Bericht verwijderen
          </f7-button>
        </f7-block>
      </template>
    </div>
  </private-gym-page>
</template>

<script>
// The IMPORTED `f7`, not `this.$f7`. MEASURED: framework7-vue 8.3.4 registers no
// global property at all — `shared/plugin.js` installs the router integration
// and nothing else — so `this.$f7` is undefined in this app and would throw here.
// `calendar.vue:362` is the shipping in-repo analog and imports `f7` the same way.
import { f7 } from "framework7-vue";
import moment from "moment";

// Dutch month names on the already-pushed body. `moment.locale()` is a GLOBAL
// mutation and four shipped files already call it (admin/news.vue:106,
// admin/broadcasts.vue:134, account/payments.vue:77, payment-single/success:17,
// and NewsComposer.vue:140, which this page imports). Calling it here as well
// means this page does not depend on another module's side effect having run.
moment.locale("nl");

import PrivateGymPage from "../../_layouts/PrivateGymPage.vue";
import NewsComposer from "../../../components/general/news/NewsComposer.vue";
import { sanitiseNewsHtml } from "../../../js/sanitise";

/**
 * B14's push, and the two things about it the author does NOT choose.
 *
 * ===========================================================================
 *  THE CATEGORY IS FORCED TO `news`, AND IT REACHES ZERO PEOPLE TODAY.
 * ===========================================================================
 * D-44-12, decided by the owner on 2026-09-01 from written alternatives:
 * a news push uses the `news` category and RESPECTS the member's preference.
 * Riding `operational` to reach everyone was offered and DECLINED.
 *
 * `news` is consent-basis and DEFAULT OFF (NotificationCategory.php:56-62,
 * Telecommunicatiewet art. 11.7), and no member has opted in — so the first news
 * push resolves an audience and then reaches ZERO recipients. THAT IS THE
 * CONSENT MODEL WORKING, NOT A DEFECT. The count dialog's zero variant is the
 * correct and expected result, and it offers no send control, which is exactly
 * right. Do not "fix" a zero by widening the category, by defaulting the
 * preference on, or by routing through `operational`.
 *
 * ===========================================================================
 *  THE AUDIENCE IS `all_active`, AND THE AUTHOR DOES NOT PICK ONE.
 * ===========================================================================
 * [CLAUDE DEFAULT — pending owner review.] A news item is a gym-wide
 * announcement, so D-44-09's union is the audience it is about. B14 is one block
 * with one button (44-UI-SPEC.md § B14) and has no picker, so the alternative
 * would be a second audience selector on a second screen — two places for
 * D-44-03's "never remembered" rule to drift apart. The count gate still stands
 * between the press and the send, and the dialog names the number before it
 * goes.
 */
const PUSH_CATEGORY = "news";

const PUSH_AUDIENCE = "all_active";

/**
 * The two sentences B10 authors, repeated here VERBATIM.
 *
 * B14 reuses the count dialog rather than restating the zero / one / many
 * grammar, and these are the strings that carry it. They are written twice
 * because BroadcastComposerPage.vue and this page are separate SFCs and a
 * `<style scoped>`-era Vue component cannot share a module-level constant
 * without a third file — and extracting one would move two SHIPPED source pins
 * (`BroadcastComposerForm.test.js:733` and the 4.00:1 contrast note
 * `BroadcastConfirmDialog.test.js:782-783`) that assert the text lives in that
 * component. `NewsPushBlock.test.js` therefore asserts every shared literal
 * against BOTH sources, so the two copies cannot drift apart silently.
 */
const IRREVERSIBLE_LINE = "Een verstuurd pushbericht kun je niet terughalen.";

const ZERO_RECIPIENT_TEXT =
  "Er is nu niemand in deze groep die een pushbericht kan ontvangen. "
  + "Dat kan komen doordat niemand de app op een telefoon heeft, of doordat ze dit soort bericht hebben uitgezet.";

/**
 * The push body when the author left the subtitle empty.
 *
 * [CLAUDE DEFAULT — pending owner review.] `body` is `required` on
 * ConfirmBroadcastRequest and the subtitle is `nullable` on `news`, so this case
 * is reachable. The alternative — truncating the article's HTML to 480
 * characters — would announce half a sentence the author never wrote. This says
 * nothing about the content, which is the honest thing to say when there is no
 * authored summary to send.
 */
const PUSH_BODY_FALLBACK = "Lees het nieuwe bericht in de app.";

/** The five fields the author edits, and the only five that are compared. */
const FIELDS = ["title", "subtitle", "content", "visible_from", "visible_to"];

/**
 * A news row reduced to the five editable fields, with every null flattened to
 * an empty string AND `content` run through the read-side sanitiser.
 *
 * A legacy row carries `null` for a subtitle or for either date, and binding a
 * null straight into an input renders the string `null` for the author to delete
 * by hand. Flattening here also makes the dirty comparison honest: `null` and
 * `''` are the same draft, and an author who has typed nothing must not be told
 * they have unsaved changes.
 *
 * ---------------------------------------------------------------------------
 * `content` IS SANITISED HERE, AND THIS IS A SECURITY BOUNDARY RATHER THAN A
 * TIDINESS ONE.
 * ---------------------------------------------------------------------------
 * `draft.content` is handed to `<news-composer :content>`, which forwards it to
 * `<f7-text-editor :value>`, which framework7 implements as
 *
 *   framework7-vue/components/text-editor.js:100-104
 *     watch(() => props.value, (v) => f7TextEditor.setValue(v))
 *   framework7/components/text-editor/text-editor-class.js:126-134
 *     setValue(v) { ... self.$contentEl.html(self.value) ... }
 *
 * — i.e. raw `innerHTML` into a LIVE contenteditable, with exactly the execution
 * semantics of `v-html`. Until the phase-43 adversarial review, `sanitiseNewsHtml`
 * ran only on the PREVIEW pane below, so a stored row went into the editor
 * untouched: opening `/admin/news/{id}` on a row carrying an `<img src=x
 * onerror=…>` handler ran that handler in a SUPER ADMIN's WebView, before any
 * preview and before any save, with the whole `localStorage` — bearer token
 * included — readable by it. The app's CSP is stock Cordova boilerplate
 * (`default-src *` with `unsafe-inline`), so it stops neither the execution nor
 * the exfiltration, and that token opens the gym door.
 *
 * (The exploit string itself is deliberately NOT written out here.
 * `tests/js/remote-html.test.js` refuses any literal outbound call in `src/`,
 * and it is right to: a payload in a comment is still a payload in the bundle.
 * The executable copy lives in `tests/components/news/NewsComposer.test.js`.)
 *
 * WHY HERE AND NOT IN `applyItem()`. This is the single construction path for a
 * draft — `data()` and `applyItem()` are its only two callers today — so a
 * third caller added later inherits the cleaning instead of having to remember
 * it. `tests/js/richtext-sink-coverage.test.js` is the gate that fails if it
 * ever stops happening; `tests/js/vhtml-coverage.test.js` structurally CANNOT
 * see this sink, which is why the review's exploit sat green under it.
 *
 * WHY THE WRITE PATH IS NOT ENOUGH ON ITS OWN. `HtmlSanitiser` cleans on write
 * and 43-11's runbook backfills the legacy rows, but the owner edits the
 * production `news` table by hand — a documented, current workflow — so a row
 * written after the backfill is a row no ordering protects. This is the second
 * control, and there is no third.
 *
 * NOT APPLIED TO WHAT THE AUTHOR TYPES — see `onDraftChange()`.
 *
 * @param {?Object} item
 * @returns {{title: string, subtitle: string, content: string, visible_from: string, visible_to: string}}
 */
function draftFrom(item) {
  const source = item || {};

  return FIELDS.reduce((draft, field) => {
    const value = source[field] === null || source[field] === undefined
      ? ""
      : String(source[field]);

    // `content` is the only HTML field. `title` and `subtitle` are rendered with
    // `{{ }}` everywhere they appear, which Vue escapes, and running them
    // through an HTML cleaner would turn an author's `&` into `&amp;` inside a
    // plain text input.
    draft[field] = field === "content" ? sanitiseNewsHtml(value) : value;

    return draft;
  }, {});
}

export default {
  components: { PrivateGymPage, NewsComposer },

  props: {
    f7route: Object,
    f7router: Object,
  },

  mixins: [],

  data: function () {
    return {
      /** `edit` (the form) or `preview`. The form is the default. */
      pane: "edit",

      /** The single source of truth for what the author is writing. */
      draft: draftFrom(null),

      /**
       * The draft as it was when it last matched the server, or null while there
       * is nothing to compare against yet.
       *
       * SNAPSHOTTED AFTER THE EDITOR SETTLES, never set from a change event.
       * text-editor-class.js:126-134 triggers `texteditor:change` from
       * `setValue`, and framework7-vue/components/text-editor.js:100-104 calls
       * `setValue` whenever the `value` prop changes — so loading an existing
       * item fires a change event before the author has touched anything. A
       * naive `dirty = true` handler would mark every opened item dirty, train
       * the author to dismiss the unsaved-changes dialog, and thereby protect
       * nothing. That is Phase 39's toggle bug in a different component.
       */
      baseline: null,

      /**
       * DERIVED FROM THE ROUTE at construction, not defaulted to either value.
       *
       * 43-08 defaulted its list page to `true` because `mounted()` runs after
       * the first render, so a `false` default paints the loaded state for one
       * frame. The same reasoning applies here and produces the opposite answer
       * per mode: an edit URL must start on the preloader, and a create URL must
       * never show one at all — there is nothing to wait for, and a spinner on
       * an empty form is a lie about what the screen is doing.
       */
      loading: !!(
        this.f7route
        && this.f7route.params
        && this.f7route.params.news_id
      ),

      /** Separate from `loading`, so a failed load is its own state. */
      error: false,

      /** True while a save is in flight. Drives the button, nothing else. */
      saving: false,

      /** The server's removal report from the last successful save, or null. */
      sanitisation: null,

      /**
       * The id the server gave a freshly created item.
       *
       * Without it, pressing save twice on `/admin/news/new` would post the same
       * article twice and leave two rows behind — the fields are reloaded from
       * the response, so the second press looks identical to the first.
       */
      createdId: null,

      /** The route entry this page installed its leave guard on, if any. */
      guardedRoute: null,

      /**
       * The broadcasts already recorded against THIS news item, newest first,
       * or an empty array when the read failed or has not answered yet.
       *
       * AN EMPTY ARRAY AND A FAILED READ ARE THE SAME STATE ON PURPOSE
       * (44-UI-SPEC.md B14 `error`): both render the never-pushed body, which
       * invites a send the count gate then governs rather than hiding the
       * control behind a failure.
       */
      broadcastsForItem: [],

      /** True while a confirm or a send is in flight. Drives the button alone. */
      pushing: false,

      /** The one Dutch sentence a refused SEND leaves on the block, or null. */
      pushFailure: null,
    };
  },

  computed: {
    /**
     * The id from the URL, coerced.
     *
     * `f7route.params` values are STRINGS and the `news/by_id` getter is strict
     * `===` on a number by design (43-06), so a string id silently resolves to
     * undefined. The coercion belongs here, at the boundary.
     *
     * @returns {?number}
     */
    routeNewsId() {
      const raw = this.f7route && this.f7route.params
        ? this.f7route.params.news_id
        : undefined;

      if (raw === undefined || raw === null || raw === "") {
        return null;
      }

      const parsed = parseInt(raw, 10);

      return Number.isNaN(parsed) ? null : parsed;
    },

    /** True when this page was opened on an existing item's URL. */
    isEditRoute() {
      return this.routeNewsId !== null;
    },

    /**
     * The id to write against: the URL's, or the one a create just returned.
     *
     * @returns {?number}
     */
    newsId() {
      return this.routeNewsId !== null ? this.routeNewsId : this.createdId;
    },

    /**
     * True once there is a stored row behind this screen — which is what decides
     * store-versus-update and whether delete is offered, rather than the URL.
     */
    hasStoredItem() {
      return this.newsId !== null;
    },

    /** @returns {string} the authored Dutch title for this mode */
    pageTitle() {
      return this.isEditRoute ? "Bericht bewerken" : "Nieuw bericht";
    },

    /**
     * The draft body, through the same cleaner the member's card renders with.
     *
     * A COMPUTED rather than an inline call in the template, following 43-03's
     * rule for the two other render sites: an inline call re-cleans on every
     * re-render of this page, including while typing in the title field. This is
     * the author's own markup in their own session, so it is not the exposure
     * this phase removes — but one render path is better than two, and running
     * it here is what makes the preview honest about what members will see.
     *
     * @returns {string}
     */
    previewBody() {
      return sanitiseNewsHtml(this.draft.content);
    },

    /**
     * Whether the preview has anything at all to show.
     *
     * Tags are stripped before the check so an editor that leaves `<p><br></p>`
     * behind after the author deletes everything still counts as empty — that
     * markup renders as a blank line, and a preview of a blank line is what the
     * authored empty-state copy exists to replace.
     *
     * @returns {boolean}
     */
    hasSomethingToPreview() {
      const body = this.previewBody
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/g, " ")
        .trim();

      return (
        this.draft.title.trim() !== "" ||
        this.draft.subtitle.trim() !== "" ||
        body !== ""
      );
    },

    /**
     * A COMPARISON against the baseline, never a flag.
     *
     * This is the whole mitigation for the `setValue` re-emit above: an event
     * carrying the value the editor already holds produces an identical draft
     * and therefore no dirt.
     *
     * @returns {boolean}
     */
    dirty() {
      if (this.baseline === null) {
        return false;
      }

      return FIELDS.some((field) => this.draft[field] !== this.baseline[field]);
    },

    /**
     * The newest broadcast for this item that ACTUALLY SENT, or null.
     *
     * `sent_at` IS THE FILTER, NEVER THE ROW'S EXISTENCE. `POST
     * /v1/broadcasts/confirm` writes a row and returns a token; if the operator
     * closes the dialog, that row stays behind with `sent_at` NULL and nothing
     * was sent to anybody. Twelve such rows existed on the local stack after
     * 44-10. Reading one as "already pushed" would tell the author members were
     * notified when nobody was — which is the exact failure NEWS-06's separation
     * makes possible and B14 exists to prevent.
     *
     * The server already orders newest first (`scopeNewestFirst()`, 44-06/44-07)
     * and this reads the first match rather than re-sorting: a server-side
     * ordering regression should be visible in one place, not silently corrected
     * in two.
     *
     * @returns {?Object}
     */
    lastSentBroadcast() {
      const rows = Array.isArray(this.broadcastsForItem) ? this.broadcastsForItem : [];

      return rows.find((row) => row && row.sent_at) || null;
    },

    /**
     * Which of B14's three bodies renders. EXACTLY ONE, EVERY TIME.
     *
     * `dirty` wins, and the order is the point: an author with unsaved changes
     * must be stopped whether or not a push already went out, because the push
     * carries the STORED text and the screen is showing something else.
     *
     * @returns {('dirty'|'sent'|'never')}
     */
    pushBlockBody() {
      if (this.dirty) {
        return "dirty";
      }

      return this.lastSentBroadcast === null ? "never" : "sent";
    },

    /**
     * The body itself, in the authored Dutch of 44-UI-SPEC.md's Copywriting
     * Contract.
     *
     * THE NEVER-PUSHED SENTENCE STATES NEWS-06 ON THE SCREEN WHERE IT MATTERS.
     * Publishing and notifying are separate actions, so an author who saved an
     * item has to be told that nobody has been told. Leaving it implicit is how
     * an author comes to believe they announced something they did not.
     *
     * @returns {string}
     */
    pushBlockText() {
      if (this.pushBlockBody === "dirty") {
        return "Sla je wijzigingen eerst op. De push gebruikt de opgeslagen tekst.";
      }

      if (this.pushBlockBody === "never") {
        return "Voor dit bericht is nog geen push verstuurd. "
          + "Leden krijgen pas een melding als je die hier verstuurt.";
      }

      const row = this.lastSentBroadcast;
      const count = typeof row.sent_recipient_count === "number" ? row.sent_recipient_count : null;
      const when = moment(row.sent_at, moment.ISO_8601, true);

      return "Push verstuurd op "
        + (when.isValid() ? when.format("D MMMM YYYY HH:mm") : "onbekende datum")
        + (count === null ? "." : " naar " + count + " " + this.recipientNoun(count) + ".");
    },

    /**
     * The push body: the author's own summary, or the fallback.
     *
     * The subtitle and NOT the article. `body` is capped at 480 characters by
     * ConfirmBroadcastRequest because that is the lock-screen ceiling, and the
     * article is a TEXT column holding up to 60,000 bytes — truncating it would
     * announce half a sentence.
     *
     * @returns {string}
     */
    pushBody() {
      const subtitle = (this.draft.subtitle || "").trim();

      return subtitle === "" ? PUSH_BODY_FALLBACK : subtitle;
    },

    /**
     * Whether the push button may be pressed.
     *
     * A PUSH COMPOSED FROM AN UNSAVED DRAFT WOULD ANNOUNCE TEXT THAT IS NOT
     * STORED ANYWHERE. The confirm request carries `this.draft.title`, and while
     * `dirty` is true that string is not what any member would find when they
     * followed the notification.
     *
     * @returns {boolean}
     */
    canSendPush() {
      return this.hasStoredItem && !this.dirty && !this.pushing;
    },
  },

  created() {
    //
  },

  mounted() {
    // Installed before the load, so a back gesture during a slow request is
    // still handled — by a clean page, which resolves immediately.
    this.installLeaveGuard();

    if (this.isEditRoute) {
      this.loadItem();
      this.loadBroadcastsForItem();
    } else {
      // Create mode IS the empty state. The baseline is the empty draft, so a
      // brand-new page nobody has typed into leaves without a prompt.
      this.$nextTick(() => {
        this.baseline = { ...this.draft };
      });
    }
  },

  beforeUnmount() {
    this.releaseLeaveGuard();
  },

  methods: {
    /**
     * Loads the item this page is editing.
     *
     * Retry runs through this same method, which is what returns the screen to
     * its preloader instead of spinning inside the failure block — one loading
     * representation per screen, not two.
     *
     * THE REJECTION HANDLER IS REQUIRED, NOT DECORATIVE. `news/find` falls
     * through to `news/fetch_all_admin`, which is deliberately not silent and
     * still rejects; 43-07 measured that `.finally` does not handle a rejection.
     * Without a handler the failure becomes an unhandled rejection, which is
     * console noise on top of the one message the author has already been shown.
     * It dispatches nothing of its own, which is the property the one-message
     * rule actually asks for.
     */
    loadItem() {
      this.loading = true;
      this.error = false;

      this.$store
        .dispatch("news/find", { news_id: this.routeNewsId })
        .then((item) => {
          if (!item) {
            // A URL naming a row that is not there is a failed load, not an
            // empty form: silently offering a blank create form under the title
            // "Bericht bewerken" would let the author write an article they
            // believe is an edit.
            this.error = true;

            return;
          }

          this.applyItem(item);
        })
        .catch(() => {
          this.error = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    /**
     * Replaces the draft with a stored row and re-snapshots the baseline.
     *
     * The `$nextTick` is the load-bearing part: assigning `draft.content` pushes
     * a new `value` prop into the editor, framework7-vue calls `setValue`, and
     * `setValue` fires a change event back at this page. Snapshotting after that
     * has settled is what stops a freshly opened item from being dirty.
     *
     * @param {Object} item
     */
    applyItem(item) {
      this.draft = draftFrom(item);

      this.$nextTick(() => {
        this.baseline = { ...this.draft };
      });
    },

    /**
     * The composer emits the WHOLE draft on every edit, so this is an
     * assignment rather than a merge.
     *
     * DELIBERATELY NOT SANITISED, unlike `draftFrom()`. This value came OUT of
     * the editor's own contenteditable a microsecond ago — it is already in the
     * DOM, so cleaning it here protects nothing. What it would cost is real:
     * the cleaned string differs byte-wise from what the element holds, so it
     * flows straight back through `:content` -> `:value` -> `setValue()` ->
     * `$contentEl.html()`, which replaces the element's children and returns the
     * caret to the start of the document — mid-sentence, on every keystroke the
     * cleaner normalises.
     *
     * The author's own markup is cleaned by the SERVER on write (`HtmlSanitiser`,
     * one seam, and 43-02's mutation test proves the update path shares it), and
     * the removal notice reports what went. The `previewBody` computed below is
     * what shows the author the members' view of their own draft.
     *
     * `tests/js/richtext-sink-coverage.test.js` classifies this producer as an
     * `echo` and says so by name, so the asymmetry is asserted rather than
     * merely commented.
     *
     * @param {Object} draft
     */
    onDraftChange(draft) {
      this.draft = { ...draft };
    },

    /**
     * Saves, then reloads the fields from what was actually stored.
     *
     * That reload is not an optimisation, it is the point: what the author sees
     * next must be the STORED article. If the editor kept the pre-save draft the
     * author would go on editing a document that no longer exists anywhere, and
     * the removal notice above would be describing text they cannot see.
     *
     * The rejection handler adds NO message. The server's form-request bag names
     * the offending field and `api/onError` already renders it; nothing this
     * screen could author would beat that. What the handler does do is clear the
     * in-flight flag — without it a failed save leaves the button spinning and
     * disabled forever — and stop the rejection going unhandled.
     */
    save() {
      if (this.saving) {
        return;
      }

      this.saving = true;

      // Cleared before the request rather than after it, so a report from the
      // previous save can never be read as belonging to this one.
      this.sanitisation = null;

      const payload = { ...this.draft };
      const action = this.hasStoredItem ? "news/update" : "news/store";

      if (this.hasStoredItem) {
        payload.news_id = this.newsId;
      }

      this.$store
        .dispatch(action, payload)
        .then((result) => {
          const item = (result && result.item) || {};

          if (this.createdId === null && this.routeNewsId === null && item.id) {
            this.createdId = item.id;

            // B14 appears the moment there IS a stored item, so it needs its
            // answer. A freshly created item has no broadcasts, but asking is
            // what makes the never-pushed body a READING rather than an
            // assumption -- and the same call is what a later save re-runs.
            this.loadBroadcastsForItem();
          }

          this.applyItem(item);
          this.sanitisation = (result && result.sanitisation) || null;

          this.$store.dispatch("notifications/addSuccessNotification", {
            message: "Bericht opgeslagen.",
          });
        })
        .catch(() => {
          // Deliberately silent. See the docblock above.
        })
        .finally(() => {
          this.saving = false;
        });
    },

    /**
     * B14's read: the broadcasts already recorded against this news item.
     *
     * NO PRELOADER AND NO SECOND LOADING STATE (44-UI-SPEC.md B14 `loading`).
     * The block arrives with the rest of the form and reads never-pushed until
     * this answers, which is the same string it would settle on for an item that
     * genuinely has no push. A spinner here would put a second waiting state on
     * a screen that already has one.
     *
     * THE REJECTION HANDLER IS THE B14 `error` ROW, NOT DEFENSIVE HABIT: a
     * failed read renders the never-pushed body rather than an error, because
     * that invites a send the count gate then governs instead of hiding the
     * control behind a failure. The action suppresses the toast for the same
     * reason, so this failure is silent by design.
     */
    loadBroadcastsForItem() {
      if (!this.hasStoredItem) {
        return Promise.resolve([]);
      }

      return this.$store
        .dispatch("broadcasts/fetch_for_news", { news_id: this.newsId })
        .then((rows) => {
          this.broadcastsForItem = Array.isArray(rows) ? rows : [];
        })
        .catch(() => {
          this.broadcastsForItem = [];
        });
    },

    /**
     * `ontvanger` for one, `ontvangers` for anything else.
     *
     * The same discipline B10 applies, and `ontvanger` rather than `lid`: the
     * count is who RECEIVES, not who matched the audience.
     *
     * @param {number} count
     * @returns {string}
     */
    recipientNoun(count) {
      return count === 1 ? "ontvanger" : "ontvangers";
    },

    /**
     * B14's button: runs the CONFIRM phase. NOTHING IS SENT HERE.
     *
     * It reuses `broadcasts/confirm` and `broadcasts/send` — the same two
     * super-admin-gated endpoints the broadcast composer spends — rather than
     * adding a second sending path. That is what makes CAST-06's count gate,
     * CAST-08's rate limit and CAST-07's compliance record apply to a news push
     * for free (D-44-07, T-44-55).
     *
     * The guard on `canSendPush` is the second half of the double-submit
     * protection: the button is already disabled, and this covers a press
     * arriving in the same tick.
     */
    openPushConfirm() {
      if (!this.canSendPush) {
        return;
      }

      this.pushing = true;

      // Cleared BEFORE the request, never after it: a failure block still on
      // screen while the next attempt is in flight reads as belonging to that
      // attempt.
      this.pushFailure = null;

      this.$store
        .dispatch("broadcasts/confirm", {
          audience: PUSH_AUDIENCE,
          member_id: null,
          category: PUSH_CATEGORY,
          news_id: this.newsId,
          title: this.draft.title,
          body: this.pushBody,
        })
        .then((confirmation) => {
          this.openPushCountDialog(confirmation);
        })
        .catch(() => {
          // DELIBERATELY SILENT. `broadcasts/confirm` does not suppress its
          // toast, and its 422 carries the form request's own Dutch field bag —
          // which names the offending field, and is what a title over 120
          // characters or a European price string produces. A second sentence
          // here would be two messages for one failure.
        })
        .finally(() => {
          this.pushing = false;
        });
    },

    /**
     * B10, reused: the count the server produced, in front of the author.
     *
     * EVERY STRING HERE IS BroadcastComposerPage.vue'S, VERBATIM. B14 does not
     * restate the zero / one / many grammar; `NewsPushBlock.test.js` asserts each
     * shared literal against BOTH sources so the two copies cannot drift.
     *
     * @param {{audience_size: number, recipient_count: number, confirmation_token: string}} confirmation
     */
    openPushCountDialog(confirmation) {
      const count = this.serverRecipientCount(confirmation);

      if (count === null) {
        // THE SERVER PRODUCED NO COUNT, so no dialog opens. D-44-02 forbids this
        // client defaulting one, and defaulting to 0 would select the zero
        // variant — whose authored text is a CLAIM ABOUT THE AUDIENCE the server
        // never made. Found by falsification in 44-10 (mutation M11).
        this.pushFailure = "Versturen is niet gelukt. Probeer het opnieuw.";

        return;
      }

      const token = confirmation.confirmation_token;
      const noun = this.recipientNoun(count);
      const size = confirmation ? confirmation.audience_size : null;

      const buttons = count === 0
        // THE ZERO VARIANT OFFERS NO SEND CONTROL AT ALL. `Verstuur naar 0
        // ontvangers` would do nothing, and a control that does nothing teaches
        // an operator to press through the dialog. On this screen the zero is
        // also the EXPECTED first result (D-44-12).
        ? [{ text: "Sluiten" }]
        : [
          { text: "Annuleren" },
          {
            text: "Ja, versturen naar " + count + " " + noun,

            // Red because the audience is `all_active`, and for no other reason
            // — B10 paints red on that audience alone so the signal is not
            // drained. Colour is never the only signal: the label states the
            // count and the verb.
            color: "red",
            onClick: () => {
              // DEFERRED BY ONE TICK. dialog-class.js:92-95 runs this handler
              // BEFORE it closes the dialog, so dispatching here would put the
              // one irreversible request in flight underneath a dialog that is
              // still on screen.
              this.$nextTick(() => {
                this.performPushSend(token);
              });
            },
          },
        ];

      const dialog = f7.dialog.create({
        title: count === 0 ? "Niemand ontvangt dit bericht" : "Naar " + count + " " + noun,
        text: count === 0
          ? ZERO_RECIPIENT_TEXT
          : (typeof size === "number" && size !== count
            ? "In deze groep zitten " + size + " leden. "
              + count + " daarvan kunnen nu een pushbericht ontvangen. "
            : "")
            + IRREVERSIBLE_LINE,

        // A backdrop tap here means "not now", which is the safe direction:
        // nothing has been sent and the item is untouched.
        closeByBackdropClick: true,

        // MANDATORY, NOT A PREFERENCE. `--f7-dialog-width` is 270px at every
        // viewport and `verticalButtons` defaults to false (dialog-class.js:15),
        // so two horizontal buttons get about 135px each.
        verticalButtons: true,

        // AND STACKING ALONE IS NOT ENOUGH — 44-10 measured `Ja, versturen naar
        // 280 ontvangers` at scrollWidth 271 / clientWidth 270 and it ellipsised.
        // This is the same scoped class, and the rule lives in the shipped
        // src/css/components/dialogs.scss.
        cssClass: "broadcast-count-dialog",

        buttons,
      });

      dialog.open();
    },

    /**
     * The server's recipient count, or `null` when the response carried none.
     *
     * NEVER DEFAULTED TO A NUMBER (D-44-02). A missing count is a contract
     * regression rather than a designed state.
     *
     * @param {?Object} confirmation
     * @returns {?number}
     */
    serverRecipientCount(confirmation) {
      const count = confirmation ? confirmation.recipient_count : null;

      return typeof count === "number" && isFinite(count) ? count : null;
    },

    /**
     * Spends the token. THIS IS THE IRREVERSIBLE CALL.
     *
     * NO SUCCESS TOAST. The block itself flips to the already-pushed body with
     * the date and the count on it, which is a persistent report rather than one
     * that fades in five seconds — and it is the same sentence the author will
     * read the next time they open the item. A toast here would be a second,
     * worse copy of it.
     *
     * The token is a parameter rather than component state, so nothing survives
     * the dialog that a later path could spend twice.
     *
     * @param {string} token
     */
    performPushSend(token) {
      if (this.pushing) {
        return;
      }

      this.pushing = true;
      this.pushFailure = null;

      this.$store
        .dispatch("broadcasts/send", { confirmation_token: token })
        .then(() => {
          // RE-READ RATHER THAN CONSTRUCT. The row the block renders is the
          // server's own record of what was sent -- the date and the count come
          // from `broadcasts`, not from the send response held in a variable.
          return this.loadBroadcastsForItem();
        })
        .catch((rejection) => {
          this.pushFailure = this.describePushFailure(rejection);
        })
        .finally(() => {
          this.pushing = false;
        });
    },

    /**
     * One Dutch sentence for a refused send.
     *
     * `broadcasts/send` suppresses the global toast (IC8: a failed send gets
     * exactly one message), so this line is that message. `api.js:157` rejects
     * with the axios RESPONSE, so `status` is read straight off it; a network
     * failure has no response and falls to the generic sentence, which is
     * correct.
     *
     * NO `Retry-After` MINUTES HERE, and that is measured rather than lazy:
     * `api.privategym/config/cors.php:28` is `'exposed_headers' => []`, so
     * `getResponseHeader('Retry-After')` is null in every browser (DEF-44-04).
     * 44-10 shipped the minutes branch on the broadcast composer and observed it
     * unreachable. Authoring it again here would add a second dead branch.
     *
     * @param {?Object} rejection
     * @returns {string}
     */
    describePushFailure(rejection) {
      const status = rejection && typeof rejection.status === "number" ? rejection.status : null;

      if (status === 429) {
        return "Je hebt net al een bericht verstuurd. Probeer het over een paar minuten opnieuw.";
      }

      return "Versturen is niet gelukt. Probeer het opnieuw.";
    },

    /**
     * Asks before deleting, in Dutch.
     *
     * `f7.dialog.create` with explicit labels, never Framework7's own one-line
     * confirm helper, which renders its untranslated OK/Cancel defaults
     * (dialog.js:9-10). The destructive button is second and red; the safe one
     * is first. The title is quoted so a 191-character one still reads as the
     * object of the sentence rather than running into the copy around it.
     */
    confirmDelete() {
      const dialog = f7.dialog.create({
        title: "Bericht verwijderen",
        text:
          'Weet je zeker dat je "' +
          this.draft.title +
          '" wilt verwijderen? Leden zien dit bericht daarna niet meer. ' +
          "Dit kan niet ongedaan worden gemaakt.",
        closeByBackdropClick: true,
        buttons: [
          { text: "Annuleren" },
          {
            text: "Verwijderen",
            color: "red",
            onClick: () => {
              // DEFERRED BY ONE TICK, deliberately. dialog-class.js:92-95 runs
              // this handler BEFORE it closes the dialog, so dispatching here
              // would put the request in flight underneath a dialog that is
              // still on screen. There is no spinner inside a dialog on this
              // screen, and this is what keeps that true.
              this.$nextTick(() => {
                this.destroyItem();
              });
            },
          },
        ],
      });

      dialog.open();
    },

    /**
     * Deletes, then returns to the list.
     *
     * On failure the global handler's toast is the only message, the composer
     * stays open with the item intact so the operator can retry, and the dialog
     * is NOT re-opened automatically — a confirmation that reappears by itself
     * is a confirmation nobody reads.
     */
    destroyItem() {
      this.$store
        .dispatch("news/destroy", { news_id: this.newsId })
        .then(() => {
          this.$store.dispatch("notifications/addSuccessNotification", {
            message: "Bericht verwijderd.",
          });

          // Released first: the item is gone, so the draft still sitting in this
          // component is not "unsaved changes" and must not raise a prompt on
          // the way out.
          this.releaseLeaveGuard();
          this.f7router.back("/admin/news", { force: true });
        })
        .catch(() => {
          // Deliberately silent; the global handler has already spoken.
        });
    },

    /**
     * Attaches the unsaved-changes check to this page's own route entry.
     *
     * ONE SEAM FOR ALL THREE WAYS OUT. Framework7 runs `from.route.beforeLeave`
     * inside `processRouteQueue` (modules/router/process-route-queue.js:53-60),
     * and both `navigate()` and `back()` go through it
     * (navigate.js:797, back.js:593 and :705). So the navbar's back link, the
     * Android hardware back — which capacitor-app.js:67-71 turns into
     * `currentView.router.back()` — and a panel entry that navigates the main
     * view all arrive here. Writing three separate handlers would be three
     * places for the check to drift apart.
     *
     * It is also the only cancellable seam: `page:beforeout` fires too late to
     * stop anything.
     */
    installLeaveGuard() {
      const route = this.f7route && this.f7route.route;

      if (!route) {
        return;
      }

      this.guardedRoute = route;
      route.beforeLeave = this.beforeLeave;
    },

    /**
     * Detaches it again, so the hook cannot outlive this page.
     *
     * The route entry is the object declared in `src/js/routes.js` and is shared
     * by every visit, so leaving a closed-over handler on it would keep this
     * instance alive and guard the next visitor's navigation with the previous
     * author's draft.
     */
    releaseLeaveGuard() {
      if (this.guardedRoute && this.guardedRoute.beforeLeave === this.beforeLeave) {
        delete this.guardedRoute.beforeLeave;
      }

      this.guardedRoute = null;
    },

    /**
     * The unsaved-changes prompt.
     *
     * The content this composer replaces is a ~4,500-character newsletter, and
     * losing it to a stray back-swipe on a phone is not an acceptable failure
     * mode. A clean page resolves immediately and shows nothing, which is what
     * keeps the dialog meaningful on the occasions it does appear.
     *
     * @param {{resolve: Function, reject: Function}} context Framework7's router queue callbacks
     */
    beforeLeave(context) {
      if (!this.dirty) {
        context.resolve();

        return;
      }

      const dialog = f7.dialog.create({
        title: "Niet opgeslagen wijzigingen",
        text: "Je hebt wijzigingen die nog niet zijn opgeslagen.",

        // A dialog dismissed by tapping the backdrop would call neither handler,
        // and Framework7 has already set `allowPageChange = false` — so the
        // router would wait forever and the app would stop navigating. One of
        // the two buttons must be pressed.
        closeByBackdropClick: false,
        buttons: [
          { text: "Blijven", onClick: () => context.reject() },
          {
            text: "Stoppen zonder opslaan",
            color: "red",
            onClick: () => {
              this.baseline = { ...this.draft };
              context.resolve();
            },
          },
        ],
      });

      dialog.open();
    },
  },

  watch: {
    //
  },
};
</script>

<style scoped>
/*
  THE SAME CLASS NAME AS src/pages/admin/news.vue, and the same two declarations.

  A Vue `<style scoped>` block cannot be shared between two SFCs, so 43-08's rule
  cannot be imported here — the alternative is a global stylesheet, which would
  mean editing a page this plan does not own and moving a rule 43-08's test
  asserts as source text. Keeping the name identical is what stops the two
  drifting apart under different names; a row is logged in deferred-items.md.

  672px sits at --f7-card-expandable-tablet-width (670px), the widest the
  member's reading surface can ever be, so the author's line breaks and paragraph
  rhythm are honest about how the article will land — and the preview and the
  editor share one measure. Below 768px the page is full-bleed, exactly as every
  other page in the app is.
*/
@media (min-width: 768px) {
  .admin-news-column {
    max-width: 672px;
    margin-left: auto;
    margin-right: auto;
  }
}

/*
  The Heading role, taken entirely from Framework7's own tokens. No numeric font
  size is authored anywhere on this page.
*/
.admin-news-heading {
  font-size: var(--f7-block-title-font-size);
  font-weight: var(--f7-block-title-font-weight);
  line-height: var(--f7-block-title-line-height);
  margin-top: 0;
  margin-bottom: 8px;
}

/* The Display role for the preview's own headline. */
.news-preview-title {
  font-size: var(--f7-block-title-medium-font-size);
  font-weight: var(--f7-block-title-medium-font-weight);
  line-height: var(--f7-block-title-medium-line-height);
  margin-top: 0;
  margin-bottom: 8px;
}

/* The Label role, in the inherited secondary text colour. */
.news-preview-subtitle {
  font-size: var(--f7-block-footer-font-size);
  color: var(--f7-list-item-text-text-color);
  margin-top: 0;
  margin-bottom: 16px;
}
</style>
